/**
 * ListActionBar Component
 *
 * A reusable action bar for list views with refresh button and customizable
 * "new item" button (supports dropdown menu for multiple options).
 *
 * @example
 * ```tsx
 * // Simple new button
 * <ListActionBar
 *   onRefresh={handleRefresh}
 *   refreshing={isRefreshing}
 *   newButtonLabel="New Assessment"
 *   onNewClick={handleNew}
 * />
 *
 * // Dropdown with multiple options
 * <ListActionBar
 *   onRefresh={handleRefresh}
 *   refreshing={isRefreshing}
 *   newButtonLabel="New Assessment"
 *   newButtonOptions={[
 *     { label: 'Nursing', color: 'bg-blue-500', onClick: () => handleNew('Nursing') },
 *     { label: 'PT', color: 'bg-green-500', onClick: () => handleNew('PT') },
 *   ]}
 * />
 * ```
 */
import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, Plus, ChevronDown } from 'lucide-react';

export interface NewButtonOption {
  /** Display label for the option */
  label: string;
  /** Optional description text */
  description?: string;
  /** Optional badge nodes rendered below the label/description */
  badges?: React.ReactNode;
  /** Color indicator (CSS class like 'bg-blue-500') */
  color?: string;
  /** Click handler for this option */
  onClick: () => void;
  /** Whether this option is disabled */
  disabled?: boolean;
}

export interface ListActionBarProps {
  /** Callback when refresh is clicked */
  onRefresh?: () => void;
  /** Whether data is currently refreshing */
  refreshing?: boolean;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Label for the "new" button */
  newButtonLabel?: string;
  /** Click handler for simple new button (without dropdown) */
  onNewClick?: () => void;
  /** Options for dropdown menu (overrides onNewClick) */
  newButtonOptions?: NewButtonOption[];
  /** Whether the new button is disabled */
  newButtonDisabled?: boolean;
  /** Whether to show the refresh button (default: true) */
  showRefresh?: boolean;
  /** Whether to show the new button (default: true when newButtonLabel is provided) */
  showNew?: boolean;
  /** Additional content to render in the action bar */
  children?: React.ReactNode;
  /** Align custom content to the end of the action bar */
  alignChildren?: 'start' | 'end';
  /** Filter toggle rendered between refresh and new actions */
  filterToggle?: React.ReactNode;
  /** Optional CSS class for the container */
  className?: string;
}

const ListActionBar: React.FC<ListActionBarProps> = ({
  onRefresh,
  refreshing = false,
  loading = false,
  newButtonLabel,
  onNewClick,
  newButtonOptions,
  newButtonDisabled = false,
  showRefresh = true,
  showNew,
  children,
  alignChildren = 'start',
  filterToggle,
  className = '',
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Determine if we should show the new button
  const shouldShowNew = showNew ?? (newButtonLabel !== undefined);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Handle dropdown option click
  const handleOptionClick = (option: NewButtonOption) => {
    if (!option.disabled) {
      option.onClick();
      setDropdownOpen(false);
    }
  };

  // Handle new button click
  const handleNewButtonClick = () => {
    if (newButtonOptions && newButtonOptions.length > 0) {
      setDropdownOpen(!dropdownOpen);
    } else if (onNewClick) {
      onNewClick();
    }
  };

  // Only disable refresh button during loading/refreshing
  // New button should remain active
  const isRefreshDisabled = refreshing || loading;

  return (
    <div className={`flex flex-col sm:flex-row ${children ? 'sm:justify-between' : 'sm:justify-end'} sm:items-center mb-2 space-y-0 ${className}`}>
      {/* Custom children content */}
      <div className={`order-2 mt-3 w-full sm:order-1 sm:mt-0 sm:w-auto ${alignChildren === 'end' ? 'sm:ml-auto' : ''}`}>
        {children}
      </div>

      {/* Action buttons container */}
      <div className="order-1 flex w-full items-center justify-end space-x-2 sm:order-2 sm:w-auto">
        {filterToggle}

        {/* Refresh Button */}
        {showRefresh && onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshDisabled}
            title="Refresh"
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center">
              <RefreshCw size={16} className={`mr-0 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </div>
          </button>
        )}

        {/* New Button */}
        {shouldShowNew && newButtonLabel && (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={handleNewButtonClick}
              disabled={newButtonDisabled}
              title={`Create ${newButtonLabel}`}
              className={`w-full sm:w-auto px-4 py-2 rounded-md text-sm transition-colors whitespace-nowrap flex items-center justify-center ${
                newButtonDisabled
                  ? 'bg-slate-400 text-gray-600 cursor-not-allowed'
                  : 'bg-slate-800 hover:bg-slate-700 text-white'
              }`}
            >
              <Plus size={16} className="mr-2" />
              <span>{newButtonLabel}</span>
              {newButtonOptions && newButtonOptions.length > 0 && (
                <ChevronDown size={16} className={`ml-1 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              )}
            </button>

            {/* Dropdown Menu */}
            {newButtonOptions && newButtonOptions.length > 0 && dropdownOpen && (
              <div className="absolute right-0 sm:right-0 left-0 sm:left-auto mt-2 w-full sm:w-60 bg-white rounded-lg shadow-lg z-20 overflow-hidden border border-gray-200">
                <div className="py-1">
                  {newButtonOptions.map((option, index) => (
                    <button
                      type="button"
                      key={index}
                      className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 flex items-center transition-colors ${
                        option.disabled ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      onClick={() => handleOptionClick(option)}
                      disabled={option.disabled}
                    >
                      {option.color && (
                        <span className={`h-3 w-3 rounded ${option.color} mr-3 flex-shrink-0`}></span>
                      )}
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{option.label}</span>
                          {option.badges && (
                            <div className="flex items-center gap-1">
                              {option.badges}
                            </div>
                          )}
                        </div>
                        {option.description && (
                          <span className="text-xs text-gray-500">{option.description}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListActionBar;
